/**
 * Document Upload Hook
 * Manages document upload state, progress tracking, and API-based uploads.
 *
 * Two modes:
 * - Existing conversation: uploads immediately, marks done after storage upload (non-blocking)
 * - New conversation (isFirstMessage): file-selection-only; files are held until message send
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../redux/store';
import {
  uploadDocument,
  validateFiles,
  generateDocumentId,
  getUploadErrorMessage,
  markDocumentsAsDeleted
} from '../services/documentUploadService';
import {
  UseDocumentUploadReturn,
  UploadState,
  FileUploadStatus,
} from '../types/documentUpload';

/**
 * Hook for managing document uploads
 */
export const useDocumentUpload = (
  conversationId: string,
  messageId: string,
  isFirstMessage = false,
): UseDocumentUploadReturn => {
  const user = useSelector((state: RootState) => state.user.user);

  // State
  const [uploadState, setUploadState] = useState<UploadState>('idle');
  const [fileStatuses, setFileStatuses] = useState<FileUploadStatus[]>([]);

  // Refs for upload management
  const listenersRef = useRef<Map<string, () => void>>(new Map());
  const timeoutTimersRef = useRef<Map<string, NodeJS.Timeout>>(new Map());
  const abortControllerRef = useRef<AbortController | null>(null);

  // Cleanup function
  const cleanup = useCallback(() => {
    listenersRef.current.forEach((unsubscribe) => {
      unsubscribe();
    });
    listenersRef.current.clear();

    timeoutTimersRef.current.forEach((timeout) => {
      clearTimeout(timeout);
    });
    timeoutTimersRef.current.clear();

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  // Calculate total progress
  const totalProgress = fileStatuses.length > 0
    ? fileStatuses.reduce((acc, file) => acc + file.progress, 0) / fileStatuses.length
    : 0;

  // Check if any file is uploading (not yet done/error)
  const isUploading = fileStatuses.some(file =>
    file.status === 'uploading'
  );

  /**
   * Select files for upload
   */
  const selectFiles = useCallback((files: FileList | File[]) => {
    const fileArray = Array.from(files);
    const validation = validateFiles(fileArray);

    const newFileStatuses: FileUploadStatus[] = [];

    validation.valid.forEach(file => {
      const id = generateDocumentId();
      newFileStatuses.push({
        id,
        file,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        status: 'idle',
        progress: 0
      });
    });

    validation.invalid.forEach(({ file, error }) => {
      const id = generateDocumentId();
      newFileStatuses.push({
        id,
        file,
        name: file.name,
        size: file.size,
        mimeType: file.type,
        status: 'error',
        progress: 0,
        error
      });
    });

    setFileStatuses(prev => [...prev, ...newFileStatuses]);

    if (validation.valid.length > 0) {
      setUploadState('idle');
    }
  }, []);

  /**
   * Remove a file from the list
   */
  const removeFile = useCallback((fileId: string) => {
    const listener = listenersRef.current.get(fileId);
    if (listener) {
      listener();
      listenersRef.current.delete(fileId);
    }

    setFileStatuses(prev => prev.filter(f => f.id !== fileId));
  }, []);

  /**
   * Update file status
   */
  const updateFileStatus = useCallback((
    fileId: string,
    updates: Partial<FileUploadStatus>
  ) => {
    setFileStatuses(prev => prev.map(file =>
      file.id === fileId ? { ...file, ...updates } : file
    ));
  }, []);

  /**
   * Start uploading all selected files.
   *
   * For new conversations (isFirstMessage), this is a no-op — files are held
   * in local state and will be sent as part of the multipart conversation start.
   *
   * For existing conversations: uploads to storage, marks done immediately
   * (non-blocking — doesn't wait for RAG processing).
   */
  const startUpload = useCallback(async () => {
    // In new-conversation mode, files are held for multipart send — no upload here
    if (isFirstMessage) {
      // Mark all idle files as "done" immediately so the dialog closes
      setFileStatuses(prev => prev.map(f =>
        f.status === 'idle' && !f.error
          ? { ...f, status: 'done' as const, progress: 100 }
          : f
      ));
      setUploadState('done');
      return;
    }

    if (!user?.id) {
      console.error('[DocumentUpload] No authenticated user');
      return;
    }

    const filesToUpload = fileStatuses.filter(
      f => f.status === 'idle' && !f.error
    );

    if (filesToUpload.length === 0) {
      return;
    }

    setUploadState('uploading');
    abortControllerRef.current = new AbortController();

    for (const fileStatus of filesToUpload) {
      if (abortControllerRef.current?.signal.aborted) {
        break;
      }

      try {
        updateFileStatus(fileStatus.id, { status: 'uploading' });

        // Upload via API — server handles storage + metadata creation
        const result = await uploadDocument(
          fileStatus.file,
          conversationId,
          messageId,
          fileStatus.id,
          (progress) => {
            updateFileStatus(fileStatus.id, { progress });
          }
        );

        // Non-blocking: mark as done immediately after storage upload
        // RAG processing continues in the background
        updateFileStatus(fileStatus.id, {
          status: 'done',
          progress: 100,
          storagePath: result.storage_path
        });

      } catch (error: any) {
        console.error('[DocumentUpload] Upload error:', error);
        const errorMessage = getUploadErrorMessage(error);
        updateFileStatus(fileStatus.id, {
          status: 'error',
          error: errorMessage
        });
      }
    }

    // All uploads dispatched — check final state
    setUploadState('done');
  }, [user, fileStatuses, conversationId, messageId, updateFileStatus, isFirstMessage]);

  /**
   * Cancel all uploads
   */
  const cancelUpload = useCallback(() => {
    const uploadedDocIds = fileStatuses
      .filter(f => f.status === 'uploading' || f.status === 'processing' || f.status === 'done')
      .map(f => f.id);

    if (uploadedDocIds.length > 0 && user?.id) {
      markDocumentsAsDeleted(user.id, uploadedDocIds).catch(error => {
        console.error('[DocumentUpload] Failed to mark cancelled uploads as deleted:', error);
      });
    }

    cleanup();

    setFileStatuses(prev => prev.map(file =>
      file.status === 'uploading' || file.status === 'processing'
        ? { ...file, status: 'idle', progress: 0 }
        : file
    ));

    setUploadState('idle');
  }, [cleanup, fileStatuses, user]);

  /**
   * Clear all files
   */
  const clearFiles = useCallback(() => {
    cleanup();
    setFileStatuses([]);
    setUploadState('idle');
  }, [cleanup]);

  return {
    // State
    uploadState,
    fileStatuses,
    isUploading,
    totalProgress,

    // Actions
    selectFiles,
    removeFile,
    startUpload,
    cancelUpload,
    clearFiles,

    // Validation
    validateFiles
  };
};
